import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * When this value changes while the fallback is showing, the boundary
   * resets and re-renders its children. Wire it to the route pathname (see
   * RouteErrorBoundary in App.tsx) so back/forward navigation escapes the
   * error screen instead of trapping the user until a hard refresh —
   * class boundaries keep `hasError` forever otherwise.
   */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.state.hasError &&
      this.props.resetKey !== undefined &&
      this.props.resetKey !== prevProps.resetKey
    ) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleBack = () => {
    // resetKey (route change) clears the error state once the navigation
    // lands; without a resetKey the reload fallback still works.
    window.history.back();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const serif = { fontFamily: '"Playfair Display", Georgia, serif' };
      const sans = {
        fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
      };
      const mono = {
        fontFamily: '"SF Mono", ui-monospace, monospace',
        letterSpacing: '0.18em',
      };

      return (
        <div className="fixed inset-0 overflow-y-auto bg-[#F8F6F1] dark:bg-[#1B1B1A] flex items-center justify-center p-6">
          <div className="max-w-[520px] w-full text-center">
            <p
              className="text-[10px] uppercase text-[#7A7267] dark:text-[#B6BFC7] mb-4"
              style={mono}
            >
              Unexpected error
            </p>
            <h1
              className="text-[34px] leading-[1.15] text-[#2B2B2B] dark:text-[#F2EEE5] mb-3"
              style={serif}
            >
              Something went wrong.
            </h1>
            <p
              className="text-[15px] leading-[1.6] text-[#4A5B6A] dark:text-[#B6BFC7] mb-8"
              style={sans}
            >
              The page hit a problem it couldn't recover from. Nothing you did
              caused this — going back or reloading usually clears it.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={this.handleBack}
                className="px-6 py-3 bg-[#BF5B3F] text-white text-[14px] rounded-md hover:bg-[#2B2B2B] dark:hover:bg-[#F2EEE5] dark:hover:text-[#1B1B1A] transition-colors"
                style={sans}
              >
                Go back
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="px-6 py-3 border border-[rgba(43,43,43,0.25)] dark:border-[rgba(242,238,229,0.25)] text-[#2B2B2B] dark:text-[#F2EEE5] text-[14px] rounded-md hover:border-[#BF5B3F] hover:text-[#BF5B3F] dark:hover:text-[#BF5B3F] transition-colors bg-transparent"
                style={sans}
              >
                Reload page
              </button>
            </div>

            {/* Error details render in development builds only — internal
                errors never reach customer-facing copy. */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-10 text-left">
                <summary
                  className="cursor-pointer text-[12px] text-[#7A7267] hover:text-[#BF5B3F]"
                  style={sans}
                >
                  Error details (development only)
                </summary>
                <pre className="mt-2 p-3 bg-[#E8E4DF] dark:bg-[#2B2B2B] rounded text-[11px] overflow-auto max-h-40 text-left text-[#2B2B2B] dark:text-[#F2EEE5]">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
